import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import { Link } from 'react-router-dom';
import { Button, Alert } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import { editIntegration, createIntegration } from 'utils/integrations';
import { IntegrationIcon } from '../../../integration_settings/integrationIcon';
import {
  integrationCatalogKey,
  integrationInfoForCode
} from '../../../integration_settings/integrationsInfo';
import { McpInstallGuideButton } from '../../../integration_settings/mcpInstallGuideButton';

import styles from './index.module.css';
import { onArticleClick } from '../../../../utils/HelpArticleButton';

const cx = classnames.bind(styles);

export default class Integration extends React.Component {
  static propTypes = {
    img: PropTypes.shape({
      className: PropTypes.string,
      alt: PropTypes.string
    }),
    title: PropTypes.string,
    code: PropTypes.string,
    icon: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    articleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    about: PropTypes.string,
    primaryButton: PropTypes.string,
    orgId: PropTypes.number,
    integrationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    intInfo: PropTypes.object,
    isConnected: PropTypes.bool,
    setParentState: PropTypes.func,
    isLoading: PropTypes.bool,
    zapierData: PropTypes.shape({
      id: PropTypes.number,
      organization_id: PropTypes.number
    }),
    integrationAllowed: PropTypes.bool.isRequired,
    billingRights: PropTypes.bool.isRequired,
    subscriptionLink: PropTypes.string.isRequired,
    navigate: PropTypes.func.isRequired
  };

  navigateToZapierScreen = () => {
    const { navigate, orgId, integrationId } = this.props;

    if (integrationId) {
      navigate(`/organizations/${orgId}/integrations/${integrationId}/edit`);
      return;
    }

    navigate(`/organizations/${orgId}/integrations/new/zapier`);
  };

  getPrimaryButton = tempData => {
    const {
      primaryButton,
      code,
      orgId,
      integrationId,
      isLoading,
      zapierData
    } = this.props;
    const link = `/organizations/${orgId}/integrations/`;
    const intId = tempData ? tempData.id : integrationId;
    const edit = '/edit';
    const managePath = intId
      ? `/organizations/${orgId}/integrations/${intId}${edit}`
      : `/organizations/${orgId}/integrations/new/${code}`;
    const zapierProps = {
      onClick: this.navigateToZapierScreen,
      loading: isLoading,
      children: primaryButton
    };

    const harvestProps = {
      onClick: intId
        ? () => editIntegration(orgId, intId)
        : () => createIntegration(orgId, code),
      loading: isLoading,
      children: primaryButton
    };

    const getLinks = () => {
      let newOrEdit = link;
      if (intId) {
        if (primaryButton === 'Manage') {
          newOrEdit = managePath;
          return [newOrEdit];
        }
      } else {
        newOrEdit = managePath;
      }
      return [newOrEdit];
    };

    const buttonProps = (anchorFlag, child) => {
      return {
        type: 'primary',
        className: cx({
          connectManage: true,
          connectManageAnchor: anchorFlag
        })
      };
    };

    const getButton = (props, anchorFlag, child) => {
      return (
        <Button {...buttonProps(anchorFlag, child)} {...props}>
          {child}
        </Button>
      );
    };
    const anchor = (href, string) => <a href={href}>{string}</a>;

    const getChildren = links => {
      if (links.length) {
        return <Link to={links[0]}>{getButton({}, false, primaryButton)}</Link>;
      }
      return getButton({}, true, primaryButton);
    };

    if (code === 'zapier') {
      return getButton(zapierProps, false, primaryButton);
    }
    if (code === 'harvest' && !intId) {
      return getButton(harvestProps, false, primaryButton);
    }

    if (code === 'harvest' && primaryButton !== 'Manage') {
      return getButton({}, false, anchor(link + intId + edit, primaryButton));
    }

    if (code === 'harvest') {
      return getChildren(getLinks());
    }

    return getChildren(getLinks());
  };

  getSecondaryButton = () => {
    const { isConnected, code, articleId } = this.props;
    let elevioArticleId;

    switch (code) {
      case 'zapier':
        elevioArticleId = 33883;
        break;
      case 'slack_legacy':
        elevioArticleId = 84823;
        break;
      case 'github':
        elevioArticleId = 33875;
        break;
      case 'bcx':
        elevioArticleId = 33886;
        break;
      case 'harvest':
        elevioArticleId = 84964;
        break;
    }
    elevioArticleId = articleId || elevioArticleId;

    if (isConnected) {
      return null;
    }

    if (!elevioArticleId) {
      return null;
    }

    return (
      <Button
        type="secondary"
        onClick={() => onArticleClick({ articleId: elevioArticleId })}
        className={styles.findOutMoreButton}
      >
        Find out more
      </Button>
    );
  };

  render() {
    const {
      about,
      title,
      code,
      zapierData,
      billingRights,
      integrationAllowed,
      subscriptionLink,
      icon: iconProp
    } = this.props;
    const catalogKey = integrationCatalogKey(code);
    const integrationInfo = integrationInfoForCode(code);
    const icon = iconProp || integrationInfo.icon;
    const isMcpIntegration = integrationInfo.kind === 'mcp';

    return (
      <div
        className={cx({
          integrationBox: true,
          connectedBox: this.props.isConnected,
          firstBox: code === 'zapier'
        })}
      >
        {this.props.isConnected && (
          <div className={styles.connected}>
            <CheckCircleFilled className={styles.connectedIcon} />
            <span className={styles.connectedLabel}>Connected</span>
          </div>
        )}
        <div className={styles.logoBox}>
          <IntegrationIcon
            name={catalogKey}
            icon={icon}
            integrationName={catalogKey}
          />
        </div>
        <div className={styles.moreInfo}>
          <span className={styles.title}>{title}</span>
          <p className={styles.about}>{about}</p>
          {!integrationAllowed && !isMcpIntegration ? (
            <div className={styles.alertBox}>
              <Alert
                type="warning"
                className={styles.upgradeMessage}
                message={
                  <div className={styles.alertMessage}>
                    {title} isn’t available on your plan.{' '}
                    {billingRights ? (
                      <a href={subscriptionLink} className={styles.upgradeLink}>
                        Upgrade your plan
                      </a>
                    ) : (
                      'Contact your account owner to upgrade your account.'
                    )}
                  </div>
                }
              />
            </div>
          ) : (
            <div className={styles.controlsBox}>
              {isMcpIntegration ? (
                <McpInstallGuideButton
                  articleId={integrationInfo.articleId as number}
                  clientName={integrationInfo.label || title || code}
                  buttonProps={{ shape: 'round', type: 'primary' }}
                />
              ) : (
                <>
                  {this.getPrimaryButton(zapierData)}
                  {this.getSecondaryButton()}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
}
